import { useEffect, useRef, useState } from 'react';
import { fromFiles, toFiles } from '../backup.js';
import { unzip, zip } from '../zip.js';
import { downloadBlob, stamp } from '../download.js';
import { useRepo } from '../useStore.js';
import { safeName } from '../backup.js';

export default function BackupMenu({ space }) {
  const repo = useRepo();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(null);
  const wrap = useRef(null);
  const zipInput = useRef(null);
  const dirInput = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = e => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  async function exportZip(spaceId) {
    setBusy('Packing…');
    setNote(null);
    try {
      await repo.flush();
      const files = toFiles(repo.records(), { spaceId });
      const blob = await zip(files);
      const label = spaceId ? safeName(space.name, 'space') : 'everything';
      downloadBlob(blob, `longhand-${label}-${stamp()}.zip`);
      setNote({ ok: true, text: `Exported ${files.length - 1} files.` });
    } catch (e) {
      setNote({ ok: false, text: e.message });
    } finally {
      setBusy('');
    }
  }

  async function importFiles(fileList) {
    const picked = [...fileList];
    if (!picked.length) return;
    setBusy('Reading…');
    setNote(null);
    try {
      let entries;
      if (picked.length === 1 && /\.zip$/i.test(picked[0].name)) {
        entries = await unzip(picked[0]);
      } else {
        entries = await Promise.all(
          picked.filter(f => f.name.endsWith('.md'))
            .map(async f => ({ path: f.webkitRelativePath || f.name, text: await f.text() }))
        );
      }
      const { records, report } = fromFiles(entries);
      const merged = repo.merge(records);
      await repo.flush();
      setNote({
        ok: true,
        text: `${merged.created} added, ${merged.updated} updated, ${merged.skipped} already current.`
          + (merged.conflicts
              ? ` ${merged.conflicts} of yours would have been overwritten — kept as conflict copies.`
              : '')
          + (report.warnings.length ? ' ' + report.warnings.join(' ') : '')
      });
    } catch (e) {
      setNote({ ok: false, text: e.message });
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="backup" ref={wrap}>
      <button
        className="ghost-btn" aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >{busy || 'Backup'}</button>

      {open && (
        <div className="menu backup-menu" role="menu">
          <button className="menu-row" role="menuitem" onClick={() => exportZip(null)}>
            <span className="name">Export everything</span>
            <span className="kind">.zip of .md</span>
          </button>
          {space && (
            <button className="menu-row" role="menuitem" onClick={() => exportZip(space.id)}>
              <span className="name">Export “{space.name}”</span>
              <span className="kind">.zip of .md</span>
            </button>
          )}
          <div className="menu-rule" />
          <button className="menu-row" role="menuitem" onClick={() => zipInput.current?.click()}>
            <span className="name">Import a backup…</span>
            <span className="kind">.zip</span>
          </button>
          <button className="menu-row" role="menuitem" onClick={() => dirInput.current?.click()}>
            <span className="name">Import a folder…</span>
            <span className="kind">.md files</span>
          </button>
          <p className="menu-note">
            Merges by id. Newer wins, and anything of yours it would overwrite is
            kept beside it as a conflict copy.
          </p>
          {note && <p className={'menu-note' + (note.ok ? ' is-ok' : ' is-bad')}>{note.text}</p>}
        </div>
      )}

      <input
        ref={zipInput} type="file" accept=".zip,application/zip" hidden
        aria-label="Import a backup zip"
        onChange={e => { importFiles(e.target.files); e.target.value = ''; }}
      />
      <input
        ref={dirInput} type="file" webkitdirectory="" directory="" multiple hidden
        aria-label="Import a folder of markdown"
        onChange={e => { importFiles(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}
